import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { 
  Clock, Calendar, BarChart2, PieChart, Coffee, 
  FileText, AlertCircle, RefreshCw, CheckCircle, X,
  Calendar as CalendarIcon, Clock as ClockIcon,
  Activity, Building2, Timer, Calendar as CalendarFull,
  DollarSign, TrendingUp, User, ChevronDown, ChevronUp
} from 'lucide-react';
import { 
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, PieChart as RechartsPieChart, Pie, Cell, 
  LineChart, Line
} from 'recharts';
import { useTimezone } from '../../../../hooks/useTimezone';

const HoursStats = () => {
  const { currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL || 'https://ksp3plhq.com/api';
  
  // Get user's timezone (normalized abbreviation -> IANA identifier)
  const userTimezone = useTimezone();
  
  // State for time period
  const [timePeriod, setTimePeriod] = useState('today'); // 'today', 'week', 'month'
  
  // State for data 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for labor data
  const [summaryStats, setSummaryStats] = useState({
    totalHours: 0,
    totalSessions: 0,
    avgHoursPerDay: 0,
    avgBreakDuration: 0,
    avgLunchDuration: 0,
    breaksPerDay: 0
  });
  
  // State for chart data
  const [dailyHoursData, setDailyHoursData] = useState([]);
  const [topActivities, setTopActivities] = useState([]);
  const [topBusinessUnits, setTopBusinessUnits] = useState([]);
  
  // State for MyTime data
  const [hasMyTimeEntries, setHasMyTimeEntries] = useState(false);
  const [myTimeData, setMyTimeData] = useState(null);
  const [showMyTimeSection, setShowMyTimeSection] = useState(true);
  
  // Colors for charts
  const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899'];
  
  // Safe number conversion utility
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };
  
  // Helper function to get date in user's timezone
  const getDateInUserTimezone = (offset = 0) => {
    const now = new Date();
    const userDate = new Date(now.toLocaleString("en-US", {timeZone: userTimezone}));
    userDate.setDate(userDate.getDate() + offset);
    return userDate.toISOString().split('T')[0];
  };
  
  // Calculate date ranges based on selected time period using timezone
  const getDateRange = () => {
    const today = new Date(new Date().toLocaleString("en-US", {timeZone: userTimezone}));
    let startDate, endDate;
    
    switch (timePeriod) {
      case 'today':
        startDate = getDateInUserTimezone();
        endDate = getDateInUserTimezone();
        break;
      case 'week':
        // Last 7 days
        startDate = getDateInUserTimezone(-6);
        endDate = getDateInUserTimezone();
        break;
      case 'month':
        // Current month
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate = monthStart.toISOString().split('T')[0];
        endDate = getDateInUserTimezone();
        break;
      default:
        startDate = getDateInUserTimezone();
        endDate = getDateInUserTimezone();
    }
    
    return {
      startDate,
      endDate
    };
  };
  
  // Fetch all required data
  useEffect(() => {
    const fetchAllData = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { startDate, endDate } = getDateRange();
        
        // Fetch labor stats
        await fetchLaborStats(startDate, endDate);
        
        // Fetch daily hours data
        await fetchDailyHours(startDate, endDate);
        
        // Fetch top activities and business units
        await fetchActivitiesAndBusinessUnits(startDate, endDate);
        
        // Fetch break and lunch stats
        await fetchBreakAndLunchStats(startDate, endDate);
        
        // Check for MyTime entries
        await checkMyTimeEntries(startDate, endDate);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, [currentUser, timePeriod]);
  
  // Fetch labor stats
  const fetchLaborStats = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/labor/stats?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&group_by=day&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch labor stats');
      }
      
      const data = await response.json();
      
      // Calculate total hours worked
      let totalSeconds = 0;
      let sessionCount = 0;
      
      data.stats.forEach(day => {
        const timeMatch = day.total_hours.match(/(\d+):(\d+):(\d+)/);
        if (timeMatch) {
          const hours = safeNumber(timeMatch[1]);
          const minutes = safeNumber(timeMatch[2]);
          const seconds = safeNumber(timeMatch[3]);
          totalSeconds += hours * 3600 + minutes * 60 + seconds;
          sessionCount += safeNumber(day.total_entries);
        }
      });
      
      const totalHours = safeNumber(totalSeconds / 3600);
      const avgHoursPerDay = data.stats.length > 0 ? safeNumber(totalHours / data.stats.length) : 0;
      
      setSummaryStats(prev => ({
        ...prev,
        totalHours,
        totalSessions: sessionCount,
        avgHoursPerDay
      }));
      
    } catch (err) {
      console.error('Error fetching labor stats:', err);
      throw err;
    }
  };
  
  // Fetch daily hours data
  const fetchDailyHours = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/labor/stats?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&group_by=day&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch daily hours');
      }
      
      const data = await response.json();
      
      // Transform data for chart
      const chartData = data.stats.map(day => {
        const timeMatch = day.total_hours.match(/(\d+):(\d+):(\d+)/);
        let hours = 0;
        
        if (timeMatch) {
          hours = safeNumber(timeMatch[1]) + (safeNumber(timeMatch[2]) / 60);
        }
        
        // Format date for display in user's timezone
        const date = new Date(day.period);
        const formattedDate = date.toLocaleDateString('en-US', { 
          weekday: 'short',
          month: 'short', 
          day: 'numeric',
          timeZone: userTimezone
        });
        
        return {
          date: formattedDate,
          hours: safeNumber(parseFloat(hours.toFixed(2))),
          target: 8 // Assuming 8 hours is the target
        };
      });
      
      setDailyHoursData(chartData);
      
    } catch (err) {
      console.error('Error fetching daily hours:', err);
      throw err;
    }
  };
  
  // Fetch activities and business units
  const fetchActivitiesAndBusinessUnits = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all labor entries for the period
      const response = await fetch(
        `${API_URL}/labor?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&limit=1000&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch labor entries');
      }
      
      const data = await response.json();
      const entries = data.entries || [];
      
      // Process activities
      const activityMap = new Map();
      
      // Process business units
      const businessUnitMap = new Map();
      
      // Calculate total seconds for percentage calculation
      let totalSeconds = 0;
      
      entries.forEach(entry => {
        if (!entry.clock_out_time || entry.activity === 'BREAK' || entry.activity === 'LUNCH') {
          return; // Skip entries without clock_out_time or break/lunch
        }
        
        const clockInTime = new Date(entry.clock_in_time).getTime();
        const clockOutTime = new Date(entry.clock_out_time).getTime();
        const durationSeconds = (clockOutTime - clockInTime) / 1000;
        
        totalSeconds += durationSeconds;
        
        // Add to activity map
        if (entry.activity && entry.activity !== 'IDLE') {
          const currentActivity = activityMap.get(entry.activity) || 0;
          activityMap.set(entry.activity, currentActivity + durationSeconds);
        }
        
        // Add to business unit map
        if (entry.business_unit && entry.business_unit !== 'IDLE') {
          const currentBU = businessUnitMap.get(entry.business_unit) || 0;
          businessUnitMap.set(entry.business_unit, currentBU + durationSeconds);
        }
      });
      
      // Convert maps to arrays and sort
      const activityArray = Array.from(activityMap).map(([name, seconds]) => ({
        name,
        hours: safeNumber(parseFloat((seconds / 3600).toFixed(2))),
        percentage: safeNumber(parseFloat(((seconds / totalSeconds) * 100).toFixed(2)))
      })).sort((a, b) => b.hours - a.hours);
      
      const businessUnitArray = Array.from(businessUnitMap).map(([name, seconds]) => ({
        name,
        hours: safeNumber(parseFloat((seconds / 3600).toFixed(2))),
        percentage: safeNumber(parseFloat(((seconds / totalSeconds) * 100).toFixed(2)))
      })).sort((a, b) => b.hours - a.hours);
      
      // Take top 3
      setTopActivities(activityArray.slice(0, 3));
      setTopBusinessUnits(businessUnitArray.slice(0, 3));
      
    } catch (err) {
      console.error('Error fetching activities and business units:', err);
      throw err;
    }
  };
  
  // Fetch break and lunch stats
  const fetchBreakAndLunchStats = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all labor entries for the period
      const response = await fetch(
        `${API_URL}/labor?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&limit=1000&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch labor entries');
      }
      
      const data = await response.json();
      const entries = data.entries || [];
      
      // Count break and lunch entries
      let breakCount = 0;
      let lunchCount = 0;
      let totalBreakSeconds = 0;
      let totalLunchSeconds = 0;
      
      // Group entries by date to calculate breaks per day
      const dateMap = new Map();
      
      entries.forEach(entry => {
        if (!entry.clock_out_time) {
          return; // Skip entries without clock_out_time
        }
        
        const clockInTime = new Date(entry.clock_in_time).getTime();
        const clockOutTime = new Date(entry.clock_out_time).getTime();
        const durationSeconds = (clockOutTime - clockInTime) / 1000;
        
        // Track entries by date for breaks per day calculation
        const entryDate = entry.date;
        if (!dateMap.has(entryDate)) {
          dateMap.set(entryDate, { breakCount: 0, lunchCount: 0 });
        }
        
        if (entry.activity === 'BREAK') {
          breakCount++;
          totalBreakSeconds += durationSeconds;
          dateMap.get(entryDate).breakCount++;
        } else if (entry.activity === 'LUNCH') {
          lunchCount++;
          totalLunchSeconds += durationSeconds;
          dateMap.get(entryDate).lunchCount++;
        }
      });
      
      // Calculate averages
      const avgBreakDuration = breakCount > 0 ? safeNumber(totalBreakSeconds / breakCount / 60) : 0; // in minutes
      const avgLunchDuration = lunchCount > 0 ? safeNumber(totalLunchSeconds / lunchCount / 60) : 0; // in minutes
      
      // Calculate breaks per day
      let totalDaysWithBreaks = 0;
      let totalBreaksCount = 0;
      
      dateMap.forEach(({ breakCount }) => {
        if (breakCount > 0) {
          totalDaysWithBreaks++;
          totalBreaksCount += breakCount;
        }
      });
      
      const breaksPerDay = totalDaysWithBreaks > 0 ? safeNumber(totalBreaksCount / totalDaysWithBreaks) : 0;
      
      setSummaryStats(prev => ({
        ...prev,
        avgBreakDuration,
        avgLunchDuration,
        breaksPerDay
      }));
      
    } catch (err) {
      console.error('Error fetching break and lunch stats:', err);
      throw err;
    }
  };
  
  // Check if user has MyTime entries
  const checkMyTimeEntries = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      
      // Check for MyTime entries
      const mytimeResponse = await fetch(
        `${API_URL}/mytime?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&limit=1&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!mytimeResponse.ok) {
        throw new Error('Failed to check MyTime entries');
      }
      
      const mytimeData = await mytimeResponse.json();
      const hasEntries = mytimeData.entries && mytimeData.entries.length > 0;
      setHasMyTimeEntries(hasEntries);
      
      if (hasEntries) {
        // Fetch MyTime summary
        await fetchMyTimeSummary(startDate, endDate);
      }
      
    } catch (err) {
      console.error('Error checking MyTime entries:', err);
      // Don't throw here, just silently fail since MyTime is optional
      setHasMyTimeEntries(false);
    }
  };
  
  // Fetch MyTime summary
  const fetchMyTimeSummary = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API_URL}/mytime/summary?employee_id=${currentUser.id}&start_date=${startDate}&end_date=${endDate}&group_by=day&timezone=${encodeURIComponent(userTimezone)}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch MyTime summary');
      }
      
      const data = await response.json();
      
      // Calculate totals
      const totalMyTimeHours = safeNumber(data.totals?.totalHours || 0);
      const billableHours = safeNumber(data.totals?.totalBillableHours || 0);
      const billablePercentage = totalMyTimeHours > 0 ? safeNumber((billableHours / totalMyTimeHours * 100)) : 0;
      
      setMyTimeData({
        totalHours: totalMyTimeHours,
        billableHours,
        nonBillableHours: safeNumber(data.totals?.totalNonBillableHours || 0),
        billablePercentage,
        summaries: data.summaries || []
      });
      
    } catch (err) {
      console.error('Error fetching MyTime summary:', err);
      // Don't throw here, just silently fail since MyTime is optional
    }
  };
  
  // Format hours for display
  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return '0h 0m';
    
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    return `${wholeHours}h ${minutes}m`;
  };
  
  // Refresh data
  const handleRefresh = () => {
    const { startDate, endDate } = getDateRange();
    
    setLoading(true);
    setError(null);
    
    // Fetch all data again
    Promise.all([
      fetchLaborStats(startDate, endDate),
      fetchDailyHours(startDate, endDate),
      fetchActivitiesAndBusinessUnits(startDate, endDate),
      fetchBreakAndLunchStats(startDate, endDate),
      checkMyTimeEntries(startDate, endDate)
    ])
      .then(() => {
        setSuccess('Data refreshed successfully');
        setTimeout(() => setSuccess(null), 3000);
      })
      .catch(err => {
        console.error('Error refreshing data:', err);
        setError('Failed to refresh data. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };
  
  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-800 p-2 border border-slate-700 rounded shadow-md text-xs">
          <p className="text-slate-300 font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
              {entry.name === 'hours' ? ' hours' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-200">Hours & Stats</h2>
        
        <div className="flex space-x-2">
          {/* Time Period Selector */}
          <div className="flex bg-slate-700 rounded-md overflow-hidden">
            <button
              onClick={() => setTimePeriod('today')}
              className={`px-4 py-2 text-sm font-medium ${
                timePeriod === 'today' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-300 hover:bg-slate-600'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setTimePeriod('week')}
              className={`px-4 py-2 text-sm font-medium ${
                timePeriod === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-300 hover:bg-slate-600'
              }`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => setTimePeriod('month')}
              className={`px-4 py-2 text-sm font-medium ${
                timePeriod === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-300 hover:bg-slate-600'
              }`}
            >
              This Month
            </button>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-3 py-2 bg-slate-700 hover:bg-slate-600 
                     text-slate-300 rounded-md transition-colors text-sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-900/30 border border-red-800 text-red-300 px-4 py-3 rounded-md flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
          <button 
            onClick={() => setError(null)} 
            className="ml-auto text-red-300 hover:text-red-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-900/30 border border-green-800 text-green-300 px-4 py-3 rounded-md flex items-start">
          <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>{success}</div>
          <button 
            onClick={() => setSuccess(null)} 
            className="ml-auto text-green-300 hover:text-green-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Main Content */}
      {loading && !dailyHoursData.length ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {/* Summary Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Hours Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-300">Total Hours</h3>
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold text-slate-200">
                {formatHours(safeNumber(summaryStats.totalHours))}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Total hours for the selected period
              </p>
            </div>
            
            {/* Total Sessions Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-300">Clock-in Sessions</h3>
                <CalendarIcon className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold text-slate-200">{safeNumber(summaryStats.totalSessions)}</p>
              <p className="text-xs text-slate-400 mt-1">
                Total clock-in entries
              </p>
            </div>
            
            {/* Average Hours Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-300">Avg Hours/Day</h3>
                <TrendingUp className="w-5 h-5 text-yellow-400" />
              </div>
              <p className="text-2xl font-bold text-slate-200">
                {safeNumber(summaryStats.avgHoursPerDay).toFixed(2)}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Average hours worked per day
              </p>
            </div>
            
            {/* Break/Lunch Card */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-slate-300">Avg Break Time</h3>
                <Coffee className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-2xl font-bold text-slate-200">
                {safeNumber(summaryStats.avgBreakDuration) > 0 ? 
                  `${Math.round(safeNumber(summaryStats.avgBreakDuration))}m` : 
                  'No breaks'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {safeNumber(summaryStats.breaksPerDay) > 0 ? 
                  `${safeNumber(summaryStats.breaksPerDay).toFixed(1)} breaks per day` : 
                  'No break data available'}
              </p>
            </div>
          </div>
          
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Hours Chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center">
                <BarChart2 className="w-5 h-5 mr-2 text-blue-400" />
                Hours by Day
              </h3>
              
              {dailyHoursData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailyHoursData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fill: '#CBD5E1', fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        tick={{ fill: '#CBD5E1', fontSize: 12 }}
                        label={{ 
                          value: 'Hours', 
                          angle: -90, 
                          position: 'insideLeft', 
                          fill: '#CBD5E1', 
                          fontSize: 12 
                        }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="hours" name="Hours" fill="#3B82F6" />
                      <Bar dataKey="target" name="Target" fill="#64748B" opacity={0.3} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-slate-400">No data available for this period</p>
                </div>
              )}
            </div>
            
            {/* Break & Lunch Analysis */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center">
                <Coffee className="w-5 h-5 mr-2 text-purple-400" />
                Break & Lunch Analysis
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Break Stats */}
                <div className="bg-slate-700/30 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-slate-300 mb-3">Break Stats</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-slate-400">Average Duration</span>
                        <span className="text-sm font-medium text-slate-300">
                          {Math.round(safeNumber(summaryStats.avgBreakDuration))}m
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (safeNumber(summaryStats.avgBreakDuration) / 20) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Target: 15-20 minutes</p>
                    </div>
                    
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-slate-400">Frequency</span>
                        <span className="text-sm font-medium text-slate-300">
                          {safeNumber(summaryStats.breaksPerDay).toFixed(1)}/day
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2.5">
                        <div 
                          className="bg-green-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (safeNumber(summaryStats.breaksPerDay) / 3) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Allowed: 1 Lunch & 2 Breaks</p>
                    </div>
                  </div>
                </div>
                
                {/* Lunch Stats */}
                <div className="bg-slate-700/30 p-4 rounded-lg">
                  <h4 className="text-md font-medium text-slate-300 mb-3">Lunch Stats</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-slate-400">Average Duration</span>
                        <span className="text-sm font-medium text-slate-300">
                          {Math.round(safeNumber(summaryStats.avgLunchDuration))}m
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2.5">
                        <div 
                          className="bg-yellow-500 h-2.5 rounded-full" 
                          style={{ width: `${Math.min(100, (safeNumber(summaryStats.avgLunchDuration) / 60) * 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Target: 30 minutes</p>
                    </div>
                    
                    <div className="pt-4">
                      <h5 className="text-sm font-medium text-slate-300 mb-2">Time Distribution</h5>
                      {safeNumber(summaryStats.totalHours) > 0 ? (
                        <div className="flex justify-between items-center">
                          <div className="flex-1 h-8 bg-blue-500 rounded-l-lg flex items-center justify-center text-xs text-white font-medium">
                            Work: {Math.round((safeNumber(summaryStats.totalHours) - (safeNumber(summaryStats.avgBreakDuration) + safeNumber(summaryStats.avgLunchDuration)) / 60) / safeNumber(summaryStats.totalHours) * 100)}%
                          </div>
                          <div className="h-8 bg-purple-500 flex items-center justify-center text-xs text-white font-medium px-2">
                            Break: {Math.round((safeNumber(summaryStats.avgBreakDuration) / 60) / safeNumber(summaryStats.totalHours) * 100)}%
                          </div>
                          <div className="flex-1 h-8 bg-yellow-500 rounded-r-lg flex items-center justify-center text-xs text-white font-medium">
                            Lunch: {Math.round((safeNumber(summaryStats.avgLunchDuration) / 60) / safeNumber(summaryStats.totalHours) * 100)}%
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500">No data available</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Activity and Business Unit Analysis */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Activities */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center">
                <Activity className="w-5 h-5 mr-2 text-blue-400" />
                Top Activities
              </h3>
              
              {topActivities.length > 0 ? (
                <div>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPieChart>
                        <Pie
                          data={topActivities}
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          dataKey="hours"
                          nameKey="name"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {topActivities.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    {topActivities.map((activity, index) => (
                      <div key={`activity-${index}`} className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm text-slate-300">{activity.name}</span>
                        </div>
                        <div className="text-sm text-slate-400">
                          {formatHours(safeNumber(activity.hours))} ({safeNumber(activity.percentage)}%)
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-slate-400">No activity data available</p>
                </div>
              )}
            </div>
            
            {/* Top Business Units */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-green-400" />
                Top Business Units
              </h3>
              
              {topBusinessUnits.length > 0 ? (
                <div className="space-y-6">
                  {topBusinessUnits.map((bu, index) => (
                    <div key={`bu-${index}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-slate-300">{bu.name}</span>
                        <span className="text-sm text-slate-400">
                          {formatHours(safeNumber(bu.hours))} ({safeNumber(bu.percentage)}%)
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2.5">
                        <div 
                          className="h-2.5 rounded-full" 
                          style={{ 
                            width: `${safeNumber(bu.percentage)}%`,
                            backgroundColor: COLORS[index % COLORS.length]
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <p className="text-slate-400">No business unit data available</p>
                </div>
              )}
            </div>
          </div>
          
          {/* MyTime Integration - Only show if entries exist */}
          {hasMyTimeEntries && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <div 
                className="p-4 border-b border-slate-700 flex justify-between items-center cursor-pointer"
                onClick={() => setShowMyTimeSection(!showMyTimeSection)}
              >
                <h3 className="text-lg font-medium text-slate-200 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-400" />
                  MyTime Entries
                </h3>
                <button className="text-slate-400 hover:text-slate-300">
                  {showMyTimeSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
              
              {showMyTimeSection && myTimeData && (
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-700/30 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-300 mb-2">Total Hours</h4>
                      <p className="text-xl font-bold text-slate-200">
                        {formatHours(safeNumber(myTimeData.totalHours))}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Manually entered time</p>
                    </div>
                    
                    <div className="bg-slate-700/30 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-300 mb-2">Billable Hours</h4>
                      <p className="text-xl font-bold text-green-400">
                        {formatHours(safeNumber(myTimeData.billableHours))}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {safeNumber(myTimeData.billablePercentage).toFixed(0)}% of total time
                      </p>
                    </div>
                    
                    <div className="bg-slate-700/30 p-3 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-300 mb-2">Comparison</h4>
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="text-sm text-slate-400 mb-1">MyTime</div>
                          <div className="text-md font-medium text-blue-400">
                            {formatHours(safeNumber(myTimeData.totalHours))}
                          </div>
                        </div>
                        <div className="mx-2 text-slate-500">vs</div>
                        <div className="flex-1">
                          <div className="text-sm text-slate-400 mb-1">Clock Time</div>
                          <div className="text-md font-medium text-purple-400">
                            {formatHours(safeNumber(summaryStats.totalHours))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <h4 className="text-sm font-medium text-slate-300">Billable vs Non-Billable</h4>
                    <div className="flex items-center text-xs">
                      <div className="flex items-center mr-3">
                        <div className="w-3 h-3 rounded-full bg-green-500 mr-1"></div>
                        <span className="text-slate-300">Billable</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-slate-500 mr-1"></div>
                        <span className="text-slate-300">Non-Billable</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 h-8 w-full bg-slate-700 rounded-lg overflow-hidden flex">
                    <div 
                      className="h-full bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${safeNumber(myTimeData.billablePercentage)}%` }}
                    >
                      {safeNumber(myTimeData.billablePercentage) > 10 ? `${safeNumber(myTimeData.billablePercentage).toFixed(0)}%` : ''}
                    </div>
                    <div 
                      className="h-full bg-slate-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${100 - safeNumber(myTimeData.billablePercentage)}%` }}
                    >
                      {(100 - safeNumber(myTimeData.billablePercentage)) > 10 ? `${(100 - safeNumber(myTimeData.billablePercentage)).toFixed(0)}%` : ''}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HoursStats;